import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Phone, KeyRound, Loader2, ArrowLeft } from 'lucide-react';
import AdvertiseWithUs from '../components/AdvertiseWithUs';

const LOGIN_BG = 'https://images.unsplash.com/photo-1489599162946-648fa5c07f7a?auto=format&fit=crop&w=1920&q=80';

const Login = () => {
  const { sendOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState('phone'); // 'phone' | 'otp'
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSend = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setLoading(true);
    try {
      const res = await sendOtp(phone.trim());
      setInfo(res.demo_code ? `Demo code: ${res.demo_code}` : 'Code sent to your phone.');
      setStep('otp');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send OTP. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length < 4) {
      setError('Please enter the 6-digit code.');
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(phone.trim(), code.trim());
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-black text-white relative"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.85)), url(${LOGIN_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="px-6 md:px-14 py-6">
        <span
          className="text-3xl md:text-4xl font-black tracking-tight"
          style={{
            fontFamily: "'Bebas Neue', 'Oswald', Impact, sans-serif",
            color: '#e11d48',
            letterSpacing: '0.03em',
            textShadow: '0 2px 14px rgba(225,29,72,0.4)',
          }}
        >
          NOVA<span style={{ color: '#ffffff' }}>REEL</span><span style={{ color: '#e11d48' }}>TV</span>
        </span>
      </div>

      <div className="flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md bg-black/75 backdrop-blur-sm rounded-md p-8 md:p-12 shadow-2xl">
          <h1 className="text-3xl font-bold mb-6">Sign In</h1>

          {step === 'phone' ? (
            <form onSubmit={handleSend} className="space-y-4">
              <label className="block text-sm text-white/80">
                Enter your phone number to receive a verification code
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="tel"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 415 555 1234"
                  className="w-full bg-[#333] rounded pl-10 pr-4 py-3.5 text-base text-white placeholder-white/40 outline-none focus:ring-2 focus:ring-white/40 focus:bg-[#454545] transition-colors"
                />
              </div>
              <p className="text-xs text-white/50">
                Use international format including country code (e.g. +14155551234)
              </p>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[#e11d48] hover:bg-[#c9163f] disabled:opacity-60 py-3.5 rounded font-semibold text-white transition-colors"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  setError('');
                }}
                className="flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Change number
              </button>

              <div>
                <label className="block text-sm text-white/80">Verification code</label>
                <p className="text-xs text-white/50 mt-1">Sent to {phone}</p>
              </div>

              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full bg-[#333] rounded pl-10 pr-4 py-3.5 text-lg tracking-[0.5em] text-white placeholder-white/30 outline-none focus:ring-2 focus:ring-white/40 focus:bg-[#454545] transition-colors"
                />
              </div>

              {info && <p className="text-xs text-emerald-400">{info}</p>}
              {error && <p className="text-sm text-red-500">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-[#e11d48] hover:bg-[#c9163f] disabled:opacity-60 py-3.5 rounded font-semibold text-white transition-colors"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Sign In'}
              </button>

              <button
                type="button"
                onClick={handleSend}
                disabled={loading}
                className="w-full text-sm text-white/70 hover:text-white transition-colors"
              >
                Didn&apos;t receive the code? Resend
              </button>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-white/10 text-xs text-white/50">
            <span className="inline-block px-2 py-1 bg-white/10 rounded mr-2 font-semibold uppercase tracking-wide text-emerald-400">Demo</span>
            Mock OTP flow — any valid phone number works, code is <span className="font-mono text-white">123456</span>.
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-16">
        <AdvertiseWithUs variant="card" />
      </div>
    </div>
  );
};

export default Login;
